/**
 * CallHistoryData.js
 * Talkroute
 *
 * Created by Vel Genov on 12/28/18
 * Copyright (c) 2018 Talkroute. All right reserved.
 */
import { trimPhoneNumberPlusSign } from "@helpers/functions/phoneNumberFormatter";
import { CallStatus, CallType, ICallHistory, ICallHistoryv1 } from "@models/Callhistory.models";
import { IContact } from "@models/Contacts.models";
import uiString from "../../constants/uiString";
import { CallData, formatPhoneNumber } from "./CallData";
import { CallDataEvent, callHistoryEventsJSONParser } from "./CallDataEvent";

/**
 * A data class for CallHistory data
 *
 * @param calledNumber the number that was called
 * @param type the call type
 * @param callerId the callerId
 * @param numberDialed the number dialed
 * @param callResult the result of the call. Uses the callStatus enum
 * @param recorded if the call is recorded
 * @param recording string URL to the recorded file
 */
export class CallHistoryData extends CallData {
  public callerId: number;
  public numberDialed: string;
  public calledNumber: string;
  public type: CallType;
  public callResult: any;
  public recorded: boolean;
  public events: CallDataEvent[];
  public recording?: string;
  public contact: IContact | null;

  constructor(opts) {
    super(opts);

    this.calledNumber = trimPhoneNumberPlusSign(opts.calledNumber);
    this.type =
      opts.type?.toString().toLowerCase() === "inbound"
        ? CallType.INBOUND
        : CallType.OUTBOUND;
    this.callerId = opts.callerId;
    this.numberDialed = trimPhoneNumberPlusSign(opts.numberDialed);
    this.callResult = opts.callResult;
    this.recorded = opts.recorded;
    this.events = opts.events;
    this.recording = opts.recording;
    this.contact = opts.contact;
  }

  /**
   * Get the call type formatted for display
   *
   * @returns {*} a string from uiString with the call type
   */
  getCallTypeFormatted = () => {
    if (!this.type) {
      return null;
    }

    switch (this.type) {
      case CallType.INBOUND:
        return uiString.callType.INBOUND;

      case CallType.OUTBOUND:
        return uiString.callType.OUTBOUND;

      default:
        return uiString.callType.NOTAVAILABLE;
    }
  };

  /**
   * Get the call result formatted for display
   *
   * @returns {*} a string from uiString with the result
   */
  getCallResultFormatted = () => {
    if (this.type === CallType.OUTBOUND) {
      return uiString.callStatus.OUTGOING;
    }

    if (!this.callResult) {
      return null;
    }

    switch (this.callResult.toString().toLowerCase()) {
      case CallStatus.ANSWERED:
        return uiString.callStatus.ANSWERED;

      case CallStatus.MISSED:
        return uiString.callStatus.MISSED;

      case CallStatus.HANGUP:
        return uiString.callStatus.HANGUP;

      case CallStatus.CLOSED:
        return uiString.callStatus.CLOSED;

      case CallStatus.BLOCKED:
        return uiString.callStatus.BLOCKED;

      default:
        return uiString.callStatus.NOTAVAILABLE;
    }
  };

  /**
   * Determine the caller phone number and return it. The caller phone number is determined based
   * on the direction of the call. For inbound calls we use the callerNumber
   * and for outbound the calledNumber. To return the number without the plus character,
   * use `getCallerPhoneNumberWithoutPlus`
   *
   * @returns the caller phone number as a String
   */
  getCallerPhoneNumber = () => {
    // If inbound, display the callerNumber, if outbound use the calledNumber
    const callerPhoneNumber =
      this.type === CallType.INBOUND ? this.callerNumber : this.calledNumber;

    return callerPhoneNumber;
  };

  /**
   * Determine the caller phone number and return it. The caller phone number is determined based
   * on the direction of the call. For inbound calls we use the callerNumber
   * and for outbound the calledNumber
   *
   * @returns the caller phone number as a String
   */
  getCallerPhoneNumberWithoutPlus = () => {
    // If inbound, display the callerNumber, if outbound use the calledNumber
    const callerPhoneNumber =
      this.type === CallType.INBOUND ? this.callerNumber : this.calledNumber;

    return callerPhoneNumber.replace('+', '');
  };

  /**
   * If a callerCName is available, use it. If not, use the
   * caller phone number from getCallerPhoneNumber
   *
   * @returns a String with the callerCName, if available or getCallerPhoneNumber
   */
  getCallerNameOrNumber = () => {
    if (this.callerCName) return this.callerCName;

    return this.getCallerPhoneNumber();
  };

  /**
   * Return the caller contact name if available.
   * If not available, use cname, and if cname is not available - use phone number;
   * 
   * @returns contact name or cname or phone number
   */
  getContactInfo = () => {
    if (this.contact) return this.contact.name;
    return this.getCallerNameOrNumberFormatted();
  }

  /**
   * A convenience method that returns the formatted value of getCallerPhoneNumber
   *
   * @returns a formatted value of getCallerPhoneNumber
   */
  getCallerPhoneNumberFormatted = () =>
    formatPhoneNumber(this.getCallerPhoneNumber());

  /**
   * A convenience method that returns the formatted value of getCallerNameOrNumber
   *
   * @returns a formatted value of getCallerNameOrNumber
   */
  getCallerNameOrNumberFormatted = () =>
    formatPhoneNumber(this.getCallerNameOrNumber());

  /**
   * A getter for numberDialed. To get the number without the plus sign,
   * use `getVirtualPhoneNumberWithoutPlus`
   *
   * @returns Talkroute Virtual Phone Number used for Call, without formatting
   */
  getVirtualPhoneNumber = () => this.numberDialed;

  /**
   * A getter for numberDialed, without the plus
   *
   * @returns Talkroute Virtual Phone Number used for Call, without formatting
   */
  getVirtualPhoneNumberWithoutPlus = () => this.numberDialed.replace('+', '');

  /**
   * A convenience method that returns the formatted value of getVirtualPhoneNumber
   *
   * @returns a formatted value of getVirtualPhoneNumber
   */
  getVirtualPhoneNumberFormatted = () =>
    formatPhoneNumber(this.getVirtualPhoneNumber());

  /**
   * Checks if the call is recorded, valid for API v2
   * @returns boolean if the call is recorded
   */
  isCallRecorded = () => this.recorded;

  /**
   * Sets the contact name to equal the first parameter.
   * @param name
   * @returns A new instance of the class with the updated contact name
   */
  setContactInfo = (contact: IContact) => new CallHistoryData({
      date: this.date,
      callerCName: this.callerCName,
      duration: this.duration,
      id: this.id,
      callerNumber: this.callerNumber,
      calledNumber: this.calledNumber,
      type: this.type,
      callerId: this.callerId,
      numberDialed: this.numberDialed,
      callResult: this.callResult,
      recorded: this.recorded,
      events: this.events,
      recording: this.recording,
      contact: contact
    });


  /**
   * Delete recording from the call and return a new one
   * 
   */
  deleteRecording = () => {
    this.recorded = false;
    this.recording = undefined;
    return this;
  }
}

/**
 * Parses the incoming JSON call history data. Creates a CallHistoryObject for each line item
 *
 * @param {*} callHistoryData an array containig call history.
 */
export const callHistoryJSONParser = (callHistoryData: ICallHistoryv1[]) => {
  // If valid data is passed, proceed. Otherwise don't return anything
  if (Array.isArray(callHistoryData) && callHistoryData.length > 0) {
    // Process each entry
    const parsedData = callHistoryData.map((entry) => {
      const {
        date,
        caller_cname: callerCName,
        caller_number: callerNumber,
        called_number: calledNumber,
        type,
        // caller_id: callerId,
        duration,
        number_dialed: numberDialed,
        call_result: callResult,
        id,
        // recorder,
        // recording
      } = entry;
      const callHistoryEntry = new CallHistoryData({
        date,
        callerCName,
        callerNumber,
        calledNumber,
        type,
        // callerId,
        duration,
        numberDialed,
        callResult,
        id,
        // recorder,
        // recording
      });

      return callHistoryEntry;
    });

    return parsedData;
  }
  return [];
};

/**
 * Parses the incoming JSON call history data. Creates a CallHistoryObject for each line item
 * callHistoryJSONParserV2 is used to parse the response from API v2
 *
 * @param {*} callHistoryData an array containig call history.
 */
export const callHistoryJSONParserV2 = (callHistoryData: ICallHistory[]) => {
  // If valid data is passed, proceed. Otherwise don't return anything
  if (Array.isArray(callHistoryData) && callHistoryData.length > 0) {
    // Process each entry
    const parsedData = callHistoryData.map((entry) => {
      // Process events
      const {
        callDate: date,
        externalNumber: callerCName,
        direction: type,
        duration,
        result: callResult,
        id,
        recorded,
        recording,
        events: eventsData,
      } = entry;

      let calledNumber, callerNumber;
      if (type === CallType.OUTBOUND) {
        ({ externalNumber: calledNumber, phoneNumber: callerNumber } = entry);
      } else {
        ({ externalNumber: callerNumber, phoneNumber: calledNumber } = entry);
      }
      const events = callHistoryEventsJSONParser(eventsData);
      const numberDialed = entry.phoneNumber;
      
      const callHistoryEntry = new CallHistoryData({
        date,
        callerCName,
        callerNumber,
        calledNumber,
        type,
        // callerId,
        duration,
        numberDialed,
        callResult,
        id,
        recorded,
        recording,
        events,
      });

      return callHistoryEntry;
    });

    return parsedData;
  } else {
    return []
  }
  return []
};

/**
 * Parses the incoming generic objects into CallHistoryData objects.
 * Checks for empty objects passed in
 *
 * @param {*} callHistoryData an array containig call history.
 */
export const callHistoryRehydrator = (callHistoryData) => {
  // If valid data is passed, proceed. Otherwise don't return anything
  if (Array.isArray(callHistoryData) && callHistoryData.length > 0) {
    // Process each entry
    const parsedData = callHistoryData.map((entry) =>
      callHistoryRehydrateCall(entry)
    );

    return parsedData;
  }
  return [];
};

/**
 * Parses the incoming single generic object into CallHistoryData.
 * Checks for an empty object being passed in.
 *
 * @param {*} call a single generic object containing call history.
 */
export const callHistoryRehydrateCall = (call) => {
  if (call) {
    const {
      date,
      callerCName,
      callerNumber,
      calledNumber,
      type,
      callerId,
      duration,
      numberDialed,
      recording,
      callResult,
      id,
    } = call;

    const callHistoryEntry = new CallHistoryData({
      date,
      callerCName,
      callerNumber,
      calledNumber,
      type,
      callerId,
      duration,
      numberDialed,
      recording,
      callResult,
      id,
    });

    return callHistoryEntry;
  }
  return call;
};
